import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    DashboardClaim,
    ResponseCode,
    Salutation
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    PersonDetail,
    UpdatePersonDetailForCurrentUserRequest,
    UpdatePersonDetailForCurrentUserService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    Color,
    DateFormat,
    EndpointProperty,
    Masks,
    Orientation,
    Regex,
    Unmasks
} from '@lsq-frontend/common-ui';
import { Icons, WorkflowMonitorService } from '@lsq-frontend/common-ui';
import { PhoneI18nValidator, StringI18nValidator } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { kebabCase } from 'lodash';
import { filter, pluck } from 'rxjs/operators';
import { ManageUsersService } from '../../services/manage-users.service';
import { IPermissionsUiControl } from '../../settings/settings.models';

@Component({
    selector: 'profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ProfileComponent implements OnInit {
    public personKey: number = null;
    public personDetail: PersonDetail = null;
    public userHasMfaEnabled$ = this._userService.user$.pipe(
        filter((user) => !!user),
        pluck('mfaEnabled')
    );

    // Template bindings.
    public userNameForHeading = '';
    public userTitle = '';
    public isEditingUserInfo = false;
    public isSubmittingUserInfoUpdate = false;
    public claimsAllowingTwoFactorVerification = [
        DashboardClaim.FinancingBuyerPortal,
        DashboardClaim.FinancingVendorPortal
    ];

    // Form.
    public userInfoFormGroup: FormGroup;

    // Constants.
    public dateFormat = DateFormat;
    public orientation = Orientation;
    public icons = Icons;
    public color = Color;
    public masks = Masks;
    public unmasks = Unmasks;
    public featureFlags = FeatureFlags;

    public firstNameLabel = this._i18n({
        value: 'First Name',
        meaning: 'First name field in settings'
    });

    public lastNameLabel = this._i18n({
        value: 'Last Name',
        meaning: 'Last name field in settings'
    });

    public titleLabel = this._i18n({
        value: 'Title',
        meaning: 'Title field in settings'
    });

    public phoneLabel = this._i18n({
        value: 'Phone Number',
        meaning: 'Phone number field in settings'
    });

    public extensionLabel = this._i18n({
        value: 'Extension',
        meaning: 'Phone extension field in settings'
    });

    public youReminder = this._i18n({
        value: '(You)',
        meaning: 'Reminder that this profile is yours'
    });

    public updatePersonDetailForCurrentUserProperties = [
        {
            name: 'companyKey',
            type: 'Guid',
            defaultValue: null,
            validators: [Validators.required, Validators.pattern(Regex.Guid)]
        },
        {
            name: 'firstName',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, this._stringI18nValidator.maxLength(255)]
        },
        {
            name: 'middleName',
            type: 'string',
            defaultValue: null,
            validators: [this._stringI18nValidator.maxLength(255)]
        },
        {
            name: 'lastName',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, this._stringI18nValidator.maxLength(255)]
        },
        {
            name: 'title',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, this._stringI18nValidator.maxLength(255)]
        },
        {
            name: 'salutation',
            type: 'select',
            nullable: true,
            options: Salutation,
            defaultValue: null,
            validators: []
        },
        {
            name: 'phoneNumber',
            type: 'string',
            defaultValue: null,
            validators: [Validators.required, this._phoneI18nValidator.phone()]
        },
        {
            name: 'phoneNumberExtension',
            type: 'string',
            defaultValue: null,
            validators: [this._stringI18nValidator.maxLength(255)]
        }
    ];

    // Optimization.
    private _kebabCaseMap: { [key: string]: string } = {};

    constructor(
        private _manageUsersService: ManageUsersService,
        private _updatePersonDetailForCurrentUserService: UpdatePersonDetailForCurrentUserService,
        private _alertService: AlertService,
        private _workflowMonitorService: WorkflowMonitorService,
        private _i18n: I18n,
        private _stringI18nValidator: StringI18nValidator,
        private _phoneI18nValidator: PhoneI18nValidator,
        private _errorToastBlacklistService: ErrorToastBlacklistService,
        private _userService: UserService
    ) {}

    public async ngOnInit(): Promise<void> {
        // Initialize the form.
        const userInfoFormGroupHash: { [key: string]: FormControl } = {};
        this._getUpdateUserDetailForClientProperties().forEach((property) => {
            userInfoFormGroupHash[property.name] = new FormControl(
                property.defaultValue,
                property.validators
            );
        });
        this.userInfoFormGroup = new FormGroup(userInfoFormGroupHash);

        // Get the user.
        const { personDetail } = await this._manageUsersService.manageMyProfile();
        // Set `this.personDetail` and update the UI.
        this._updateUiForUserDetail(personDetail);

        // Set the form.
        this._getUpdateUserDetailForClientProperties().forEach((property) => {
            this.userInfoFormGroup.get(property.name).setValue(this.personDetail[property.name]);
        });

        // Listen for the user to dirty the form group.
        this.userInfoFormGroup.valueChanges.subscribe(() => {
            if (this.isEditingUserInfo) {
                this._workflowMonitorService.markWorkflowStarted();
            }
        });

        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${ApiRoutes.Api}/ValidatePhoneNumberVerificationCode`,
            ResponseCode.InvalidRequest
        );
    }

    public get standardPermissionsUiControls(): IPermissionsUiControl[] {
        return this._manageUsersService.activeManageUserState.standardPermissionsUiControls;
    }
    public get adminPermissionsUiControls(): IPermissionsUiControl[] {
        return this._manageUsersService.activeManageUserState.adminPermissionsUiControls;
    }

    // TODO(vtrinh): Get from this badge if the person detail has the "Edit Profiles" users claims
    public shouldDisplayOwnerBadge(): boolean {
        return this.personDetail && false;
    }

    public editUserInfo(): void {
        this.isEditingUserInfo = true;
    }

    public cancelEditUserInfo(): void {
        this.isEditingUserInfo = false;
        this._workflowMonitorService.markWorkflowFinished();
    }

    public async submitEditUserInfo(): Promise<void> {
        // Enter the 'submitting' state.
        this.isSubmittingUserInfoUpdate = true;

        // Create the request.
        const updateUserDetailForClientRequest = new UpdatePersonDetailForCurrentUserRequest();
        const formValue: UpdatePersonDetailForCurrentUserRequest = this.userInfoFormGroup.value;
        Object.keys(formValue).forEach((key) => {
            const formControlValue = this.userInfoFormGroup.get(key).value;
            if (formControlValue != null) {
                updateUserDetailForClientRequest[key] = formControlValue;
            }
        });

        // Perform the update, then get the user detail (we don't get it from the update response).
        await this._updatePersonDetailForCurrentUserService.executeOnce(
            updateUserDetailForClientRequest
        );
        const { personDetail } = await this._manageUsersService.manageMyProfile();
        this._updateUiForUserDetail(personDetail);

        // Exit the 'editing' state.
        this.isEditingUserInfo = false;
        this.isSubmittingUserInfoUpdate = false;
        this._alertService.success('Successfully updated your profile.');
        this._workflowMonitorService.markWorkflowFinished();

        // `patchValue` forgives superfluous keys (e.g. here we're getting `emailAddress` back, which isn't
        // part of the form group) and simply ignores them, while `setValue` will throw an error.
        this.userInfoFormGroup.patchValue(personDetail);
    }

    public kebabCase(inputStr: string): string {
        if (!this._kebabCaseMap[inputStr]) {
            this._kebabCaseMap[inputStr] = kebabCase(inputStr);
        }
        return this._kebabCaseMap[inputStr];
    }

    private _getUpdateUserDetailForClientProperties(): EndpointProperty[] {
        return this.updatePersonDetailForCurrentUserProperties.filter(
            (property) => property.name.indexOf('Key') === -1
        );
    }

    private _updateUiForUserDetail(personDetail: PersonDetail): void {
        this.personDetail = personDetail;
        this.userNameForHeading = `${this.personDetail.firstName} ${this.personDetail.lastName} ${this.youReminder}`;
        this.userTitle = this.personDetail.title;
    }
}
